import React, { useMemo, useState, useEffect } from 'react';

// mui components
import { Box, Typography } from '@mui/material';

// core component
import VendorInformation from './vendorViewSections/VendorInformation';
import QuestionnaireFormsView from './vendorViewSections/QuestionnaireFormsView';

// redux
import { connect, useDispatch } from 'react-redux';
import { GetOneVendor } from 'store/actions/vendorActions';
import { toast } from 'react-toastify';

import logo from 'assets/img/vendor_smart_logo.svg';
import Success from './vendorViewSections/Success';
import { UpdateVendorDetails } from 'store/actions/vendorActions';
import CircularLoader from 'components/Loaders/CircularLoader';
import { Row } from 'antd';
import { GetVendorQuestionnaireDetails } from 'store/actions/vendorActions';
import { QUESTIONNAIRE_DETAILS } from 'store/constants';
import { useLocation } from 'react-router-dom';
import { LoadingButton } from '@mui/lab';
import { ResendQuestionnaire } from 'store/actions/vendorActions';
import SuccessIcon from 'assets/img/Success.svg';
import { useMutation } from '@tanstack/react-query';
import { RequestQuestionnaire } from 'store/actions/vendorActions';
import { Loader2 } from 'lucide-react';

const VendorsView = (props) => {
    const { location, match, GetOneVendor, one_vendor, questionnaireData } = props;
    const readonly = useMemo(() => location?.state?.readonly, [props]);
    const token = useMemo(() => match.params.token, [props]);
    const { id: questionnaireValue = '', vendor = '' } = questionnaireData || {};

    const location_ = useLocation();
    const pathname = location_.pathname?.replaceAll('/vendors/', '');
    const path = pathname.split('/')?.filter(Boolean);
    const vendorId = path?.[1];

    // state
    const [page, setPage] = useState(0);
    const [loading, setLoading] = useState(true);
    const [expired, setExpired] = useState(false);

    // functions
    const getOneVendor = async (currentVendor, params) => {
        setLoading(true);
        const res = await GetOneVendor(currentVendor, params);
        setLoading(false);
        if (!res?.success) {
            toast.error(res?.message);
        }
    };

    const dispatch = useDispatch();
    const runToMutate = async (v, params) => {
        await dispatch(UpdateVendorDetails({ has_responded: false }, v, false, params));
    };

    useEffect(() => {
        if (readonly || questionnaireData?.has_confirmed_details) {
            setPage(1);
        } else {
            setPage(0);
        }
    }, [readonly, questionnaireData]);

    // Fetch vendor Info
    useEffect(() => {
        if (path?.length >= 2) {
            setLoading(false);
            return;
        }

        const fetchInfo = async () => {
            try {
                // clear redux before doing anything
                dispatch({
                    type: QUESTIONNAIRE_DETAILS,
                    payload: {},
                });
                setLoading(true);
                const res = await dispatch(
                    GetVendorQuestionnaireDetails({
                        token: token,
                    })
                );

                await getOneVendor(res?.data?.vendor, { token });
                await runToMutate(res?.data?.vendor, { token });

                if (!res.success) {
                    throw new Error(res.message || 'Failed to fetch questionnaire history');
                }
            } catch (error) {
                toast.error(error.message || 'An error occurred while fetching data');
            } finally {
                setLoading(false);
            }
        };

        if (token) {
            fetchInfo();
        }
    }, [token, dispatch, GetVendorQuestionnaireDetails]);

    useEffect(() => {
        if (path?.length >= 2) {
            setLoading(false);
            return;
        }
    }, [path]);

    const { mutate, isLoading } = useMutation({
        mutationFn: RequestQuestionnaire,
        onSuccess: () => {
            toast.success('Questionaire sent successfully');
            setExpired(true);
        },
        onError: (error) => {
            toast.error(error?.message);
        },
    });

    return (
        <>
            {loading ? (
                <Row
                    justify="center"
                    sx={{
                        width: '100%',
                        height: '100%',
                    }}
                >
                    <CircularLoader size="large" />
                </Row>
            ) : questionnaireData?.has_responded ? (
                <Success />
            ) : expired ? (
                <div className="w-full h-screen flex flex-col justify-center items-center ">
                    <div
                        className="flex flex-col  items-center w-[90%] pt-8 lg:h-[60%] rounded-[4px]"
                        style={{ boxShadow: '4px 0px 32px 0px rgba(14, 44, 102, 0.05)' }}
                    >
                        <img src={SuccessIcon} alt="success icon" width={144} height={144} />
                        <div className="my-5">
                            <h4 className="textbrandColor text-center text-xl md:text-2xl font-bold">
                                Check your inbox
                            </h4>
                            <p className="text-center textbrandColor text-[13px] mt-2 font-medium">
                                Questionnaire link has been successfully requested.
                            </p>
                        </div>
                    </div>
                </div>
            ) : path?.length >= 2 ? (
                <div className="w-full h-screen flex flex-col justify-center items-center ">
                    <div
                        className="flex flex-col  items-center w-[90%] pt-8 lg:h-[60%] rounded-[4px]"
                        style={{ boxShadow: '4px 0px 32px 0px rgba(14, 44, 102, 0.05)' }}
                    >
                        {/* <img src={SuccessIcon} alt="success icon" width={144} height={144} /> */}
                        <div className="my-5">
                            <h4 className="textbrandColor text-xl text-center md:text-2xl font-bold">Expired link!</h4>
                            <p className="text-center textbrandColor text-[13px] mt-3 font-medium">
                                The link to this questionnaire has expired.
                            </p>
                            {!isNaN(Number(vendorId || '')) ? (
                                <LoadingButton
                                    variant="contained"
                                    color="primary"
                                    type="button"
                                    onClick={() => mutate({ data: { vendor_id: Number(vendorId) } })}
                                    textTransform="none"
                                    loading={false}
                                    loadingPosition="center"
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: '16px',
                                        minheight: '38px',
                                        px: 3,
                                        textTransform: 'inherit',
                                        mt: 2,
                                    }}
                                    disabled={isLoading}
                                >
                                    {isLoading ? <Loader2 className="mr-1 h-6 w-6 animate-spin" /> : null}
                                    Request for a new questionnaire
                                </LoadingButton>
                            ) : null}
                        </div>
                    </div>
                </div>
            ) : (
                <Box>
                    {page === 0 ? (
                        <VendorInformation
                            vendor={vendor}
                            setPage={setPage}
                            one_vendor={one_vendor}
                            contentLoading={loading.content}
                        />
                    ) : (
                        <Box
                            sx={{
                                backgroundColor: '#F8FAFC',
                                height: '100vh !important',
                            }}
                        >
                            <Box sx={{ backgroundColor: '#FFFFFF', py: 2, px: 4, height: '7vh' }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        width: '50%',
                                    }}
                                >
                                    <img src={logo} alt="SmartComply logo" />
                                    <Typography sx={{ color: '#002C72', fontSize: 18, fontWeight: 700 }}>
                                        Vendor Questionaire
                                    </Typography>
                                </Box>
                            </Box>
                            <QuestionnaireFormsView
                                readonly={readonly}
                                vendor={vendor}
                                merchant={one_vendor?.merchant}
                                page={page}
                                setPage={setPage}
                                questionnaireValue={questionnaireValue}
                                one_vendor={one_vendor}
                            />
                        </Box>
                    )}
                </Box>
            )}
        </>
    );
};

const mapStateToProps = (state) => {
    return {
        one_vendor: state?.vendorReducers?.one_vendor,
        questionnaireData: state?.vendorReducers?.questionnaire_detail,
    };
};
export default connect(mapStateToProps, { GetOneVendor, ResendQuestionnaire })(VendorsView);
